import React, { useEffect, useRef, useState } from "react";
import { Check, X, DollarSign, IndianRupee, X as CloseIcon } from "lucide-react";
import "../components/css/pricing.css";

const services = [
  {
    name: "Website Development",
    description: "Custom, fast, conversion-focused websites that drive results.",
    popular: true,
    packages: {
      basic: {
        name: "Simple Static Website",
        price: { usd: "1000 - 1609", inr: "5,999 - 13,999" },
        features: [
          { text: "Fresh, modern UI design", included: true },
          { text: "Fully responsive (mobile/tablet friendly)", included: true },
          { text: "Basic animation effects", included: true },
          { text: "Limited Design Customization", included: true },
          { text: "Fully Functioning Admin Panel", included: true },
          { text: "Hosting setup & support", included: true },
          { text: "Basic SEO Setup", included: true },
          { text: "Free Hosting Setup", included: true },
          { text: "Google Friendly", included: true },
          { text: "100% Safe and Secure", included: true },
        ]
      },
      standard: {
        name: "Business/Portfolio Website",
        price: { usd: "1709 - 3509", inr: "14,999 - 32,999" },
        features: [
          { text: "Custom UI/UX design with smooth animations", included: true },
          { text: "Minimal Animations", included: true },
          { text: "Basic E-commerce Functionality", included: true },
          { text: "Fully Functioning Admin Panel", included: true },
          { text: "CMS Integration", included: true },
          { text: "Licensed images", included: true },
          { text: "Advanced SEO Setup", included: true },
          { text: "Free Hosting Setup", included: true },
          { text: "Google Friendly", included: true },
          { text: "100% Safe and Secure", included: true },
        ]
      },
      premium: {
        name: "Advanced Website",
        price: { usd: "3609+", inr: "39,999+" },
        features: [
          { text: "3D UI & advanced transitions", included: true },
          { text: "Custom UI/UX Design", included: true },
          { text: "E-commerce Ready", included: true },
          { text: "Fully Functioning Admin Panel", included: true },
          { text: "Chatbot or live chat integration", included: true },
          { text: "Advanced SEO + Analytics", included: true },
          { text: "CMS of Your Choice", included: true },
          { text: "Licensed images", included: true },
          { text: "Free Hosting Setup", included: true },
          { text: "Google Friendly", included: true },
          { text: "100% Safe and Secure", included: true },
        ]
      }
    }
  },
  {
    name: "Mobile App Development",
    description: "Native Android & iOS apps with cutting-edge technology.",
    popular: true,
    packages: {
      basic: {
        name: "UI Design Only",
        price: { usd: "1500 - 4000", inr: "7,999 - 15,999" },
        features: [
          { text: "High-quality Android or IOS UI design", included: true },
          { text: "Modern material design & UX practices", included: true },
          { text: "Custom icon & asset design", included: true },
          { text: "Color scheme & typography selection", included: true },
          { text: "Developer-ready asset delivery", included: true },
          { text: "Design-to-code handoff", included: true },
          { text: "Logo or splash screen included", included: true },
          { text: "App walkthrough/onboarding screens", included: true },
          { text: "Adapted for all device sizes", included: true },
          { text: "Consistent design system used", included: true },
        ]
      },
      standard: {
        name: "Basic App Developement",
        price: { usd: "4000 - 12000", inr: "19,999 - 29,999" },
        features: [
          { text: "Cross-Platform Development", included: true },
          { text: "Advanced UI/UX Design", included: true },
          { text: "Up to 25 Screens", included: true },
          { text: "User Authentication", included: true },
          { text: "Push Notifications", included: true },
          { text: "App Store Submission", included: true },
          { text: "Backend Integration", included: true },
          { text: "Analytics & Crash Reporting", included: true },
        ]
      },
      premium: {
        name: "Enterprise App",
        price: { usd: "10000 - 30000+", inr: "32,999+" },
        features: [
          { text: "Native iOS & Android", included: true },
          { text: "Premium UI/UX Design", included: true },
          { text: "Unlimited Screens", included: true },
          { text: "Advanced Security Features", included: true },
          { text: "Real-time Features", included: true },
          { text: "App Store Submission", included: true },
          { text: "Custom Backend Development", included: true },
          { text: "Ongoing Support & Maintenance", included: true },
        ]
      }
    }
  },
  {
    name: "Performance Marketing & Growth Acceleration",
    description: "ROI-focused Meta & Google Ads strategies designed to scale revenue, reduce CAC, and maximize ROAS.",
    popular: true,
    packages: {
        basic: {
        name: "Weekly Trial Plan (Entry / Low Risk)",
        price: { usd: 69, inr: 1699 },
        features: [
            { text: "Campaign setup (Meta or Google)", included: true },
            { text: "1–2 campaign launches", included: true },
            { text: "Basic audience targeting", included: true },
            { text: "Pixel + conversion tracking", included: true },
            { text: "2–3 ad creatives testing", included: true },
            { text: "Basic optimization (CTR, CPC)", included: true },
            { text: "End-of-week performance summary", included: true },
        ]
        },

        standard: {
        name: "Monthly Growth Plan (Core Offer)",
        price: { usd: 269, inr: 5999 },
        features: [
            { text: "Full-funnel strategy (awareness → conversion)", included: true },
            { text: "Weekly optimization & scaling", included: true },
            { text: "Advanced audience targeting + retargeting", included: true },
            { text: "Creative testing & iteration", included: true },
            { text: "ROAS & CPL optimization", included: true },
            { text: "Weekly reports + insights", included: true },
            { text: "Email/WhatsApp support", included: true },
        ]
        },

        premium: {
        name: "Yearly Scaling Plan (Premium / High Ticket)",
        price: { usd: 3219, inr: 59999 },
        features: [
            { text: "Complete multi-platform ad strategy (Meta + Google)", included: true },
            { text: "Advanced funnel building & conversion rate optimization", included: true },
            { text: "Aggressive scaling strategy with budget control", included: true },
            { text: "High-converting ad creatives & copy optimization", included: true },
            { text: "Retargeting, lookalike & cold audience domination", included: true },
            { text: "Weekly growth strategy calls & performance reports", included: true },
            { text: "Focused on predictable & scalable business growth", included: true },
        ]
        }
    }
  },
  {
    name: "Complete Brand Identity",
    description: "Logo, visuals, and brand systems that tell your story.",
    popular: true,
    packages: {
      basic: {
        name: "Basic Logo Design",
        price: { usd: 40, inr: 699 },
        features: [
          { text: "2 unique logo concepts", included: true },
          { text: "revision included", included: true },
          { text: "PNG & JPG files", included: true },
          { text: "Clean & modern look", included: true },
          { text: "Font selection", included: true },
          { text: "Quick delivery", included: true },
          { text: "Minimalist style", included: true },
          { text: "Color variants", included: true },
          { text: "Transparent background version", included: true },
          { text: "Print-ready formats", included: true },
          { text: "Web-optimized version", included: true },
        ]
      },
      standard: {
        name: "Standard Logo Pack",
        price: { usd: 100, inr: 1499 },
        features: [
          { text: "4 professional logo concepts", included: true },
          { text: "revision included", included: true },
          { text: "PNG, JPG, SVG, and PDF files", included: true },
          { text: "Logo mockups (business card, T-shirt, etc.)", included: true },
          { text: "Font and color code document", included: true },
          { text: "Icon-based and text-based versions", included: true },
          { text: "Print and digital format", included: true },
          { text: "High-res for banners or print", included: true },
          { text: "Brand watermark version", included: true },
          { text: "Transparent background versions", included: true },
          { text: "Delivery in 1-2 working days", included: true },
        ]
      },
      premium: {
        name: "Complete Brand Identity",
        price: { usd: 599, inr: 2499 },
        features: [
          { text: "Logo + Brand Guide", included: true },
          { text: "Typography + Color System", included: true },
          { text: "Social media kit (profile, banners, etc.)", included: true },
          { text: "Business card design", included: true },
          { text: "Letterhead & favicon included", included: true },
          { text: "Multiple revisions + final tweaks", included: true },
          { text: "Branding consistency consultation", included: true },
          { text: "HD files (AI, EPS, PSD, PNG, JPG, SVG)", included: true },
          { text: "Logo watermark file pack", included: true },
          { text: "Priority delivery & support", included: true },
        ]
      }
    }
  },
];

// Package Modal
const PackageModal = ({ service, currency, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const formatPrice = (price) => {
    if (currency === 'usd') {
      return `$${price.usd.toLocaleString()}`;
    } else {
      return `₹${price.inr.toLocaleString()}`;
    }
  };

  const getPackageColor = (index) => {
    const colors = ['#0350aa', '#0770ac', '#149fa2'];
    return colors[index % colors.length];
  };

  const getPricePeriodText = (serviceName, packageKey) => {
    // Website Development packages
    if (serviceName === "Website Development") {
      if (packageKey === 'basic') return "One-time development + Domain Charges extra";
      if (packageKey === 'standard') return "One-time development + Domain Charges extra";
      if (packageKey === 'premium') return "One-time development + Domain Charges extra";
    }
    
    // Mobile App Development packages
    if (serviceName === "Mobile App Development") {
      if (packageKey === 'basic') return "One-time design fee";
      if (packageKey === 'standard') return "One-time development + Android & IOS store fees apply";
      if (packageKey === 'premium') return "One-time development + Android & IOS store fees apply";
    }
    
    // Complete Brand Identity packages
    if (serviceName === "Complete Brand Identity") {
      if (packageKey === 'basic') return "One-time design fee - Files delivered digitally";
      if (packageKey === 'standard') return "Complete brand pack - One-time payment";
      if (packageKey === 'premium') return "Full brand identity system + Source files included";
    }
    
    // Performance Marketing packages
    if (serviceName === "Performance Marketing & Growth Acceleration") {
      if (packageKey === 'basic') return "";
      if (packageKey === 'standard') return "";
      if (packageKey === 'premium') return "";
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container" ref={modalRef}>
        <div className="modal-header">
          <div className="modal-title-wrapper">
            <div>
              <h2 className="modal-title">{service.name}</h2>
              <p className="modal-subtitle">{service.description}</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <CloseIcon size={24} />
          </button>
        </div>

        <div className="packages-grid">
          {Object.entries(service.packages).map(([key, pkg], index) => (
            <div 
              key={key} 
              className="modal-package-card"
              style={{ 
                '--package-color': getPackageColor(index),
                '--index': index 
              }}
            >
              <div className="modal-package-badge" style={{ background: getPackageColor(index) }}>
                {index === 0 && 'STARTER'}
                {index === 1 && 'POPULAR'}
                {index === 2 && 'PREMIUM'}
              </div>
              
              <div className="modal-package-header">
                <h3 className="modal-package-name">{pkg.name}</h3>
              </div>

              <div className="modal-package-price">
                <span className="modal-price-amount">{formatPrice(pkg.price)}</span>
                <span className="modal-price-period">{getPricePeriodText(service.name, key)}</span>
              </div>

              <button 
                className="modal-select-btn"
                style={{ 
                  background: `linear-gradient(135deg, ${getPackageColor(index)}, ${getPackageColor(index)}dd)`
                }}
              >
                Select Package
                <span className="btn-arrow">→</span>
              </button>

              <div className="modal-features-list">
                {pkg.features.map((feature, i) => (
                  <div key={i} className="modal-feature-item">
                    <span className="modal-feature-icon">
                      {feature.included ? (
                        <Check size={16} className="feature-included" />
                      ) : (
                        <X size={16} className="feature-excluded" />
                      )}
                    </span>
                    <span className={`modal-feature-text ${!feature.included ? 'muted' : ''}`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function PricingSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [currency, setCurrency] = useState('inr');
  const [selectedService, setSelectedService] = useState(null);
  const [animatedItems, setAnimatedItems] = useState([]);
  const [headerAnimated, setHeaderAnimated] = useState(false);
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === headerRef.current && !headerAnimated) {
              setHeaderAnimated(true);
            }
            
            const index = cardRefs.current.findIndex(ref => ref === entry.target);
            if (index !== -1 && !animatedItems.includes(index)) {
              setAnimatedItems(prev => [...prev, index]);
            }
          }
        });
      },
      { 
        threshold: 0.2, 
        rootMargin: '0px',
        triggerOnce: true 
      }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    cardRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [animatedItems, headerAnimated]);

  const toggleCurrency = () => {
    setCurrency(prev => prev === 'usd' ? 'inr' : 'usd');
  };

  const openPackageModal = (service) => {
    setSelectedService(service);
  };

  const closePackageModal = () => {
    setSelectedService(null);
  };

  const getSlideDirection = (index) => {
    return index % 2 === 0 ? 'slide-left' : 'slide-right';
  };

  return (
    <section className="pricing" ref={sectionRef}>
      <div className="blob-container">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
        <div className="blob blob-4"></div>
      </div>

      <div 
        className={`pricing-header ${headerAnimated ? 'animate-fade-in' : ''}`}
        ref={headerRef}
      >
        <span className="pricing-tag">Our Services & Pricings</span>
        <h2 className="gradient-text">
          Tailored Solutions for <br /> Your Digital Success
        </h2>
        <p className="pricing-subtext">
          Select the perfect service package for your business needs. Each plan is
          crafted with precision to deliver exceptional results.
        </p>

        <div className="currency-toggle-container">
          <div className="currency-toggle" onClick={toggleCurrency}>
            <div className={`currency-option ${currency === 'inr' ? 'active' : ''}`}>
              <IndianRupee size={18} /> INR
            </div>
            <div className={`currency-option ${currency === 'usd' ? 'active' : ''}`}>
              <DollarSign size={18} /> USD
            </div>
            <div 
              className={`toggle-slider ${currency}`}
              style={{ transform: `translateX(${currency === 'inr' ? '0' : '100'}%)` }}
            />
          </div>
        </div>
      </div>

      <div className="pricing-grid">
        {services.map((service, index) => (
          <div
            key={index}
            ref={el => cardRefs.current[index] = el}
            className={`pricing-card-wrapper ${
              animatedItems.includes(index) ? `animate-in ${getSlideDirection(index)}` : ''
            }`}
            style={{ 
              animationDelay: `${index * 0.15}s`,
              transitionDelay: `${index * 0.1}s`
            }}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div
              className={`pricing-card ${service.popular ? "popular" : ""} ${
                hoveredCard === index ? "card-hovered" : ""
              }`}
            >
              {/* Blob border animation */}
              <div className="blob-border">
                <div className="blob-border-inner"></div>
              </div>

              {/* Card content */}
              <div className="card-content">
                <div className="card-header">
                  <div className="icon-wrapper">
                    {service.icon}
                  </div>
                  <h3>{service.name}</h3>
                </div>

                <p className="description">{service.description}</p>

                {/* View Packages Button */}
                <button 
                  className="view-packages-btn"
                  onClick={() => openPackageModal(service)}
                >
                  <span>View Packages</span>
                  <span className="btn-glow"></span>
                </button>

                <div className="price-container">
                  <div className="price">
                    <span className="currency">
                      {currency === 'usd' ? '$' : '₹'}
                    </span>
                    <span className="amount">
                      {currency === 'usd' 
                        ? (typeof Object.values(service.packages)[0].price.usd === 'string' 
                          ? Object.values(service.packages)[0].price.usd 
                          : Object.values(service.packages)[0].price.usd.toLocaleString())
                        : (typeof Object.values(service.packages)[0].price.inr === 'string'
                          ? Object.values(service.packages)[0].price.inr
                          : Object.values(service.packages)[0].price.inr.toLocaleString())
                      }
                    </span>
                  </div>
                  <span className="project-type">Starting from</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Package Modal */}
      {selectedService && (
        <PackageModal 
          service={selectedService} 
          currency={currency}
          onClose={closePackageModal}
        />
      )}
    </section>
  );
}